import { useEffect, useRef } from 'react';

// Keeps the device screen on for as long as the calling component is mounted
// (and `active` is true), so the screensaver/auto-lock doesn't interrupt a
// practice session. Uses the native Screen Wake Lock API — supported on
// Chrome/Edge/Android broadly and Safari/iOS 16.4+. On unsupported browsers
// this is a silent no-op; screen timeout behaves as before.
export function useWakeLock(active = true) {
    const lockRef = useRef(null);

    useEffect(() => {
        if (!active) return;
        if (!('wakeLock' in navigator)) return;

        let cancelled = false;

        const acquire = async () => {
            try {
                const lock = await navigator.wakeLock.request('screen');
                if (cancelled) {
                    lock.release().catch(() => {});
                    return;
                }
                lockRef.current = lock;
                lock.addEventListener('release', () => {
                    if (lockRef.current === lock) lockRef.current = null;
                });
            } catch {
                // Can be refused (e.g. low battery, backgrounded tab) — fail silently.
            }
        };

        acquire();

        // The OS releases the lock automatically when the tab is hidden (app
        // switch, screen already off, etc). Re-acquire it once the page is
        // visible again so returning to the session keeps the screen awake.
        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible' && !lockRef.current) {
                acquire();
            }
        };
        document.addEventListener('visibilitychange', onVisibilityChange);

        return () => {
            cancelled = true;
            document.removeEventListener('visibilitychange', onVisibilityChange);
            if (lockRef.current) {
                lockRef.current.release().catch(() => {});
                lockRef.current = null;
            }
        };
    }, [active]);
}
